import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import {
  Search,
  X,
  Filter,
  Sparkles,
  Loader2
} from 'lucide-react';
import CityAutocomplete from './CityAutocomplete';
import CountrySelector from './CountrySelector';
import AIPresets from './filters/AIPresets';
import { useCountry } from '../context/CountryContext';
import {
  VEHICLE_TYPES,
  MAKE_OPTIONS,
  getVehicleSchema,
  getFieldDef
} from '../config/vehicleFilterSchemas';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VehicleFilters = ({
  vehicleType = 'cars',
  onSearch,
  onFilterChange,
  initialFilters = {},
  resultCount = 0,
  className = ''
}) => {
  const navigate = useNavigate();
  const schema = getVehicleSchema(vehicleType);
  
  // Get country context
  const { 
    country, 
    countryConfig, 
    formatCurrency, 
    getMileageLabel,
    getPriceLabel,
    isFieldEnabled 
  } = useCountry();
  
  // Active AI preset tracking
  const [activePresetId, setActivePresetId] = useState(null);
  
  // Dynamic makes/models state
  const [dynamicMakes, setDynamicMakes] = useState([]);
  const [dynamicModels, setDynamicModels] = useState([]);
  const [makesLoading, setMakesLoading] = useState(false);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [useDynamicData, setUseDynamicData] = useState(true); // Flag to use API data
  
  // Filter state - include vehicleType in initial state
  const [filters, setFilters] = useState(() => ({
    vehicleType: vehicleType,
    make: '',
    model: '',
    location: '',
    priceMin: '',
    priceMax: '',
    yearMin: '',
    yearMax: '',
    mileageMin: '',
    mileageMax: '',
    ...initialFilters
  }));

  // Sync vehicleType prop with filter state using key reset pattern
  const currentVehicleType = vehicleType;
  if (filters.vehicleType !== currentVehicleType) {
    setFilters(prev => ({
      ...prev,
      vehicleType: currentVehicleType
    }));
  }

  // Fetch dynamic makes when country or vehicle type changes
  useEffect(() => {
    const fetchMakes = async () => {
      setMakesLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/vehicles/makes`, {
          params: {
            country: country,
            vehicle_type: vehicleType
          }
        });
        
        if (response.data.success && response.data.makes) {
          setDynamicMakes(response.data.makes);
          setUseDynamicData(true);
        } else {
          setUseDynamicData(false);
        }
      } catch (error) {
        console.error('Failed to fetch makes:', error);
        setUseDynamicData(false);
      } finally {
        setMakesLoading(false);
      }
    };

    fetchMakes();
  }, [country, vehicleType]);

  // Fetch dynamic models when make changes
  useEffect(() => {
    if (!filters.make || !useDynamicData) {
      setDynamicModels([]);
      return;
    }

    const fetchModels = async () => {
      setModelsLoading(true);
      try {
        // Find the make ID from dynamicMakes
        const selectedMake = dynamicMakes.find(
          m => m.name === filters.make || m.original_name === filters.make || m.id === filters.make
        );
        
        if (!selectedMake) {
          setDynamicModels([]);
          return;
        }

        const response = await axios.get(`${API_URL}/api/vehicles/models`, {
          params: {
            country: country,
            make_id: selectedMake.id
          }
        });
        
        if (response.data.success && response.data.models) {
          setDynamicModels(response.data.models);
        } else {
          setDynamicModels([]);
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
        setDynamicModels([]);
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  }, [filters.make, country, dynamicMakes, useDynamicData]);

  // Clear model when make changes
  useEffect(() => {
    if (!filters.make && filters.model) {
      setFilters(prev => ({ ...prev, model: '' }));
    }
  }, [filters.make]);

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      // Clear model if make is cleared or changed
      if (key === 'make') {
        newFilters.model = '';
      }
      return newFilters;
    });
  }, []);

  const clearFilters = useCallback(() => {
    const clearedFilters = {
      vehicleType: vehicleType,
      make: '',
      model: '',
      location: '',
      priceMin: '',
      priceMax: '',
      yearMin: '',
      yearMax: '',
      mileageMin: '',
      mileageMax: '',
      // Clear preset metadata
      _presetId: null,
      _presetName: null,
      _rankingWeights: null
    };
    setFilters(clearedFilters);
    setActivePresetId(null);
    setDynamicModels([]); // Clear models too
    
    // Trigger both callbacks to ensure UI and search are updated
    if (onFilterChange) {
      onFilterChange(clearedFilters);
    }
    if (onSearch) {
      onSearch(clearedFilters);
    }
  }, [vehicleType, onFilterChange, onSearch]);

  // Handle AI Preset selection
  const handleApplyPreset = useCallback((preset) => {
    // Toggle preset off if already active
    if (activePresetId === preset.id) {
      // Clear all filters and trigger search
      const clearedFilters = {
        vehicleType: vehicleType,
        make: '',
        model: '',
        location: '',
        priceMin: '',
        priceMax: '',
        yearMin: '',
        yearMax: '',
        mileageMin: '',
        mileageMax: '',
        _presetId: null,
        _presetName: null,
        _rankingWeights: null
      };
      setFilters(clearedFilters);
      setActivePresetId(null);
      
      // Trigger both callbacks
      if (onFilterChange) {
        onFilterChange(clearedFilters);
      }
      if (onSearch) {
        onSearch(clearedFilters);
      }
      return;
    }
    
    // Start with clean base filters (not existing filters)
    const presetFilters = {
      vehicleType: vehicleType,
      make: '',
      model: '',
      location: '',
      priceMin: '',
      priceMax: '',
      yearMin: '',
      yearMax: '',
      mileageMin: '',
      mileageMax: ''
    };
    
    // Apply preset filters with proper mapping
    const filterMapping = {
      bodyType: 'bodyType',
      fuelType: 'fuelType',
      transmission: 'transmission',
      seats: 'seats',
      doors: 'doors',
      colour: 'colour',
      verifiedSeller: 'verifiedSeller',
      inspectionAvailable: 'inspectionAvailable',
      runningCostBand: 'runningCostBand',
      insuranceEstimateBand: 'insuranceEstimateBand',
      mileageMax: 'mileageMax',
      ulezCompliant: 'ulezCompliant',
      bikeType: 'bikeType',
      engineCCBand: 'engineCCBand',
      boatType: 'boatType',
      configuration: 'configuration',
      permitType: 'permitType',
      registrationValid: 'registrationValid'
    };
    
    Object.entries(preset.filters).forEach(([key, value]) => {
      const mappedKey = filterMapping[key] || key;
      
      // Handle array values (multiselect filters)
      if (Array.isArray(value)) {
        presetFilters[mappedKey] = value;
      }
      // Handle object values (range filters like engineSize: { max: 1400 })
      else if (typeof value === 'object' && value !== null) {
        if (value.min !== undefined) {
          presetFilters[`${mappedKey}Min`] = value.min;
        }
        if (value.max !== undefined) {
          presetFilters[`${mappedKey}Max`] = value.max;
        }
      }
      // Handle boolean and primitive values
      else {
        presetFilters[mappedKey] = value;
      }
    });
    
    // Add preset metadata including ranking weights
    presetFilters._presetId = preset.id;
    presetFilters._presetName = preset.name;
    presetFilters._rankingWeights = preset.rankingWeights || null;
    
    setFilters(presetFilters);
    setActivePresetId(preset.id);
    
    // Trigger search with new filters (including ranking weights)
    if (onSearch) {
      onSearch(presetFilters);
    }
    if (onFilterChange) {
      onFilterChange(presetFilters);
    }
  }, [activePresetId, vehicleType, onSearch, onFilterChange]);

  const handleSearch = useCallback(() => {
    if (onSearch) {
      onSearch(filters);
    }
    if (onFilterChange) {
      onFilterChange(filters);
    }
  }, [filters, onSearch, onFilterChange]);

  // Get available makes - prefer dynamic data, fallback to static
  const makes = useMemo(() => {
    if (useDynamicData && dynamicMakes.length > 0) {
      return dynamicMakes.map(m => ({
        id: m.id,
        name: m.name,
        original_name: m.original_name
      }));
    }
    // Fallback to static options
    const staticMakes = MAKE_OPTIONS[vehicleType] || MAKE_OPTIONS.cars;
    return staticMakes.map(name => ({ id: name.toLowerCase(), name, original_name: name }));
  }, [vehicleType, dynamicMakes, useDynamicData]);

  // Get available models for selected make
  const models = useMemo(() => {
    if (useDynamicData && dynamicModels.length > 0) {
      return dynamicModels.map(m => ({
        id: m.id,
        name: m.name
      }));
    }
    return [];
  }, [dynamicModels, useDynamicData]);

  // Generate year options
  const yearOptions = useMemo(() => {
    return Array.from({ length: 35 }, (_, i) => new Date().getFullYear() - i);
  }, []);

  // Render a single filter field based on its definition
  const renderField = useCallback((fieldName) => {
    const fieldDef = getFieldDef(fieldName);
    
    switch (fieldDef.type) {
      case 'select':
        if (fieldName === 'make') {
          return (
            <div key={fieldName} className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                {fieldDef.label}
                {makesLoading && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
              </Label>
              <Select
                value={filters.make || 'any'}
                onValueChange={(value) => updateFilter('make', value === 'any' ? '' : value)}
                disabled={makesLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder={fieldDef.placeholder || 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any {fieldDef.label}</SelectItem>
                  {makes.map(make => (
                    <SelectItem key={make.id || make.name} value={make.name}>
                      {make.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        }
        return (
          <div key={fieldName} className="space-y-2">
            <Label className="text-sm font-medium">{fieldDef.label}</Label>
            <Select
              value={filters[fieldName] || 'any'}
              onValueChange={(value) => updateFilter(fieldName, value === 'any' ? '' : value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={fieldDef.placeholder || 'Select...'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {fieldDef.options?.map(opt => (
                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'multiselect':
        return (
          <div key={fieldName} className="space-y-2">
            <Label className="text-sm font-medium">{fieldDef.label}</Label>
            <div className="flex flex-wrap gap-2">
              {fieldDef.options?.map(opt => {
                const isSelected = (filters[fieldName] || []).includes(opt);
                return (
                  <Badge
                    key={opt}
                    variant={isSelected ? 'default' : 'outline'}
                    className={`cursor-pointer transition-all ${isSelected ? 'bg-blue-600' : 'hover:bg-gray-100'}`}
                    onClick={() => {
                      const current = filters[fieldName] || [];
                      const updated = isSelected
                        ? current.filter(v => v !== opt)
                        : [...current, opt];
                      updateFilter(fieldName, updated);
                    }}
                  >
                    {opt}
                  </Badge>
                );
              })}
            </div>
          </div>
        );

      case 'range':
        if (fieldName === 'priceRange') {
          return (
            <div key={fieldName} className="space-y-2">
              <Label className="text-sm font-medium">{fieldDef.label} ({fieldDef.unit})</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.priceMin || ''}
                  onChange={(e) => updateFilter('priceMin', e.target.value)}
                  className="w-full"
                />
                <span className="text-gray-400">to</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.priceMax || ''}
                  onChange={(e) => updateFilter('priceMax', e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          );
        }
        if (fieldName === 'year') {
          return (
            <div key={fieldName} className="space-y-2">
              <Label className="text-sm font-medium">{fieldDef.label}</Label>
              <div className="flex gap-2 items-center">
                <Select
                  value={filters.yearMin || 'any'}
                  onValueChange={(value) => updateFilter('yearMin', value === 'any' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="From" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-gray-400">to</span>
                <Select
                  value={filters.yearMax || 'any'}
                  onValueChange={(value) => updateFilter('yearMax', value === 'any' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="To" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        }
        if (fieldName === 'mileage') {
          return (
            <div key={fieldName} className="space-y-2">
              <Label className="text-sm font-medium">{fieldDef.label} ({fieldDef.unit})</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.mileageMin || ''}
                  onChange={(e) => updateFilter('mileageMin', e.target.value)}
                  className="w-full"
                />
                <span className="text-gray-400">to</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.mileageMax || ''}
                  onChange={(e) => updateFilter('mileageMax', e.target.value)}
                  className="w-full"
                />
              </div>
            </div>
          );
        }
        return null;

      case 'location':
        return (
          <div key={fieldName} className="space-y-2">
            <Label className="text-sm font-medium">{fieldDef.label}</Label>
            <CityAutocomplete
              value={filters.location || ''}
              onChange={(value) => updateFilter('location', value)}
              placeholder={fieldDef.placeholder || 'Any Location'}
            />
          </div>
        );

      case 'pills':
        return (
          <div key={fieldName} className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              {fieldDef.icon && <span>{fieldDef.icon}</span>}
              {fieldDef.label}
            </Label>
            <div className="flex flex-wrap gap-2">
              {fieldDef.options?.map(opt => {
                const isSelected = filters[fieldName] === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => updateFilter(fieldName, isSelected ? '' : opt)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 'toggle':
        return (
          <div key={fieldName} className="flex items-center justify-between py-2">
            <Label className="text-sm font-medium flex items-center gap-2 cursor-pointer">
              {fieldDef.icon && <span className="text-blue-600">{fieldDef.icon}</span>}
              {fieldDef.label}
            </Label>
            <Switch
              checked={filters[fieldName] || false}
              onCheckedChange={(checked) => updateFilter(fieldName, checked)}
            />
          </div>
        );

      case 'auto':
        // Auto fields are read-only, show as info
        return (
          <div key={fieldName} className="flex items-center justify-between py-2 opacity-60">
            <Label className="text-sm text-gray-500">{fieldDef.label}</Label>
            <span className="text-sm text-gray-400">Auto</span>
          </div>
        );

      default:
        return null;
    }
  }, [filters, makes, yearOptions, updateFilter]);

  return (
    <div className={`bg-white rounded-lg shadow-sm ${className}`}>
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Filter className="h-5 w-5 text-blue-600" />
            Filter & Sort
          </h2>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="h-4 w-4 mr-1" />
            Clear
          </Button>
        </div>
      </div>

      {/* Accordion Sections */}
      <Accordion 
        type="multiple" 
        defaultValue={['quick-filters', 'primary-filters', 'core-refinement']}
        className="px-4"
      >
        {/* Quick Filters (AI Presets) - TOP PRIORITY - Default Open */}
        <AccordionItem value="quick-filters">
          <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-600" />
              Quick Filters
              <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">AI</Badge>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pb-4">
            <AIPresets
              vehicleType={vehicleType}
              country={country}
              onApplyPreset={handleApplyPreset}
              activePresetId={activePresetId}
              showHeader={false}
              className=""
            />
          </AccordionContent>
        </AccordionItem>

        {/* Primary Filters - Vehicle Type, Make, Model, Location, Price */}
        <AccordionItem value="primary-filters">
          <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
            <div className="flex items-center gap-2">
              <span className="text-blue-600">🔍</span>
              Primary Filters
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            {/* Vehicle Type Selector */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Vehicle Type</Label>
              <Select
                value={filters.vehicleType || 'cars'}
                onValueChange={(value) => {
                  updateFilter('vehicleType', value);
                  navigate(`/category/${value}`);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map(type => (
                    <SelectItem key={type.slug} value={type.slug}>
                      <span className="flex items-center gap-2">
                        <span>{type.icon}</span>
                        {type.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Make & Model */}
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  Make
                  {makesLoading && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                </Label>
                <Select
                  value={filters.make || 'any'}
                  onValueChange={(value) => updateFilter('make', value === 'any' ? '' : value)}
                  disabled={makesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Any Make" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any Make</SelectItem>
                    {makes.map(make => (
                      <SelectItem key={make.id || make.name} value={make.name}>
                        {make.name}
                        {make.original_name && make.original_name !== make.name && (
                          <span className="text-xs text-gray-400 ml-1">({make.original_name})</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  Model
                  {modelsLoading && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
                </Label>
                {useDynamicData && models.length > 0 ? (
                  <Select
                    value={filters.model || 'any'}
                    onValueChange={(value) => updateFilter('model', value === 'any' ? '' : value)}
                    disabled={modelsLoading || !filters.make}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={filters.make ? "Select Model" : "Select Make First"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any Model</SelectItem>
                      {models.map(model => (
                        <SelectItem key={model.id || model.name} value={model.name}>
                          {model.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder={filters.make ? "Any Model" : "Select Make First"}
                    value={filters.model || ''}
                    onChange={(e) => updateFilter('model', e.target.value)}
                    disabled={!filters.make && useDynamicData}
                  />
                )}
              </div>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Location</Label>
              <CityAutocomplete
                value={filters.location || ''}
                onChange={(value) => updateFilter('location', value)}
                placeholder="Any Location"
              />
            </div>

            {/* Budget - Country-specific currency */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{getPriceLabel()}</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.priceMin || ''}
                  onChange={(e) => updateFilter('priceMin', e.target.value)}
                />
                <span className="text-gray-400">to</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.priceMax || ''}
                  onChange={(e) => updateFilter('priceMax', e.target.value)}
                />
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Core Refinement (Default Open) */}
        <AccordionItem value="core-refinement">
          <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
            <div className="flex items-center gap-2">
              <span className="text-blue-600">⚙️</span>
              Core Refinement
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pb-4">
            {/* Year */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Year</Label>
              <div className="flex gap-2 items-center">
                <Select
                  value={filters.yearMin || 'any'}
                  onValueChange={(value) => updateFilter('yearMin', value === 'any' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="From" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-gray-400">to</span>
                <Select
                  value={filters.yearMax || 'any'}
                  onValueChange={(value) => updateFilter('yearMax', value === 'any' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="To" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {yearOptions.map(year => (
                      <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Mileage - Country-specific unit */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">{getMileageLabel()}</Label>
              <div className="flex gap-2 items-center">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.mileageMin || ''}
                  onChange={(e) => updateFilter('mileageMin', e.target.value)}
                />
                <span className="text-gray-400">to</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.mileageMax || ''}
                  onChange={(e) => updateFilter('mileageMax', e.target.value)}
                />
              </div>
            </div>

            {/* Fuel Type */}
            {renderField('fuelType')}

            {/* Body Type (for cars) */}
            {schema.filters.secondary.includes('bodyType') && renderField('bodyType')}

            {/* Condition */}
            {renderField('condition')}
          </AccordionContent>
        </AccordionItem>

        {/* Level 4: Smart Filters - Cost, Condition & Trust */}
        <AccordionItem value="cost-condition-trust">
          <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3">
            <div className="flex items-center gap-2">
              <span className="text-green-600">💰</span>
              Cost, Condition & Trust
            </div>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-4">
            {schema.filters.smart.map(fieldName => renderField(fieldName))}
          </AccordionContent>
        </AccordionItem>

        {/* Level 5: Advanced / Technical (Collapsed) */}
        {schema.filters.advanced.length > 0 && (
          <AccordionItem value="advanced" className="border-t border-gray-100">
            <AccordionTrigger className="text-sm font-semibold hover:no-underline py-3 text-gray-500">
              <div className="flex items-center gap-2">
                <span>🔧</span>
                Advanced / Technical
              </div>
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pb-4">
              {schema.filters.advanced.map(fieldName => renderField(fieldName))}
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      {/* Search Button - Sticky at Bottom */}
      <div className="p-4 border-t bg-white sticky bottom-0">
        <Button 
          onClick={handleSearch}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
        >
          <Search className="h-4 w-4 mr-2" />
          Show {resultCount > 0 ? `${resultCount.toLocaleString()} results` : 'Results'}
        </Button>
      </div>
    </div>
  );
};

export default VehicleFilters;
